import React, { useState, useEffect, useRef } from 'react';
import { GlassCard } from '../../../_shared/GlassCard';
import { Eye, Heart, MessageCircle } from 'lucide-react';
import { WINDOW, TOTALS } from '../data';

const useCounter = (end, duration = 1200) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const hasAnimated = useRef(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated.current) {
                    hasAnimated.current = true;
                    const start = performance.now();
                    const animate = (now) => {
                        const progress = Math.min(1, (now - start) / duration);
                        const eased = 1 - Math.pow(1 - progress, 3);
                        setCount(Math.floor(end * eased));
                        if (progress < 1) requestAnimationFrame(animate);
                    };
                    requestAnimationFrame(animate);
                }
            },
            { threshold: 0.3 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [end, duration]);

    return [count, ref];
};

const StatCard = ({ icon: Icon, iconColor, label, value, sublabel }) => {
    const [animatedValue, ref] = useCounter(value || 0);
    return (
        <GlassCard className="flex flex-col justify-between min-h-[170px]">
            <div ref={ref}>
                <div className={`${iconColor} p-3 rounded-xl mb-4 inline-flex`}>
                    <Icon size={22} />
                </div>
                <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-2">{label}</p>
                <p className="text-4xl font-black text-white tracking-tight">{animatedValue.toLocaleString()}</p>
                {sublabel && (
                    <div className="mt-4">
                        <span className="inline-flex items-center gap-1 bg-purple-500/15 text-purple-300 text-xs font-bold px-2 py-0.5 rounded-full">
                            {sublabel}
                        </span>
                    </div>
                )}
            </div>
        </GlassCard>
    );
};

export const MonthHero = () => {
    return (
        <section className="min-h-[85vh] flex flex-col justify-center items-center text-center px-4 pt-24 pb-12 relative z-10">
            <div className="space-y-6 max-w-5xl mx-auto w-full">
                <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 backdrop-blur-md mb-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 mr-2 animate-pulse" />
                    <span className="text-sm font-medium text-emerald-300 tracking-wide">{WINDOW.badge}</span>
                </div>

                <div className="space-y-2">
                    <h2 className="text-lg md:text-xl text-white/60 font-medium">The Mask with Abhiveer — Full Network</h2>
                    <p className="text-sm text-white/40 font-mono">
                        {WINDOW.label} · {TOTALS.platformCount} platforms · {TOTALS.measuredAccounts} accounts measured
                    </p>
                </div>

                <h1 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white via-purple-100 to-white drop-shadow-lg leading-tight">
                    Close to a Million<br />Views in 8 Weeks.
                </h1>

                <p className="text-lg md:text-xl text-purple-200/70 max-w-2xl mx-auto leading-relaxed">
                    Everything published across the <span className="text-white font-semibold">main page and the fan network</span> on Instagram, YouTube and TikTok.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-12 w-full">
                    <StatCard icon={Eye} iconColor="bg-blue-500/20 text-blue-300" label="Views" value={TOTALS.views} sublabel="57 days, measured" />
                    <StatCard icon={Heart} iconColor="bg-rose-500/20 text-rose-300" label="Likes" value={TOTALS.likes} />
                    <StatCard icon={MessageCircle} iconColor="bg-emerald-500/20 text-emerald-300" label="Comments" value={TOTALS.comments} />
                </div>
            </div>
        </section>
    );
};
